import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { ThemeProvider } from 'styled-components';
import { Container, ScrollButtonWrapper } from './styles/ChatStyles';
import logger from './utils/logger';
import ChatHeader from './components/chat/ChatHeader';
import ChatMessages from './components/chat/ChatMessages';
import ChatInput from './components/chat/ChatInput';
import ScrollToBottom from './components/ScrollToBottom';
import LoadingScreen from './components/chat/LoadingScreen';
import { createThemeFromConfig, defaultTheme } from './themes';
import socketService from './services/socketService';

const API_BASE_URL = process.env.REACT_APP_API_URL;
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || process.env.REACT_APP_API_URL;
const PAGE_SIZE = 10;

// Configure axios defaults and interceptors
axios.defaults.timeout = 300000; // 5 minutes timeout
axios.defaults.headers.common['Cache-Control'] = 'no-cache';
axios.defaults.headers.common['Pragma'] = 'no-cache';

// Add request interceptor to add timestamp to prevent caching
axios.interceptors.request.use(
  (config) => {
    config.headers['X-Request-Time'] = Date.now().toString();
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for global error handling
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Log detailed error information
    console.error('API Error:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      config: {
        url: error.config?.url,
        method: error.config?.method
      }
    });
    return Promise.reject(error);
  }
);

const forceScroll = (element) => {
  if (element) {
    element.scrollIntoView({ block: 'end', behavior: 'instant' });
  }
};

// Helpers to split incoming content and normalise message history
const SPLIT_MARKER = '[[__CHAT_SPLIT__]]';

const splitMessageContent = (text) => {
  if (typeof text !== 'string') {
    return [];
  }

  const normalized = text.replace(/\r\n/g, '\n');
  const withMarkers = normalized.replace(/^\s*---+\s*$/gm, SPLIT_MARKER);
  return withMarkers
    .split(SPLIT_MARKER)
    .map(segment => segment.trim())
    .filter(segment => segment.length > 0);
};

const createMessageObjects = (message) => {
  if (!message) {
    return [];
  }

  const baseText = typeof message.text === 'string' ? message.text : '';
  const segments = splitMessageContent(baseText);

  if (segments.length === 0) {
    return [];
  }

  const baseId = message.id ?? `msg-${Date.now()}-${Math.random()}`;

  if (segments.length === 1) {
    return [{
      ...message,
      id: baseId,
      text: segments[0]
    }];
  }

  return segments.map((segment, index) => ({
    ...message,
    id: `${baseId}-seg${index}`,
    text: segment
  }));
};

const normalizeHistoryMessages = (historyMessages) => {
  let sequence = 0;
  const normalized = [];

  historyMessages?.forEach((msg) => {
    // Use id from API response as the primary ID
    const baseId = msg.id || `hist-${msg.create_date}`;
    const baseMessage = {
      id: baseId,
      text: msg.content ?? '',
      sent: msg.role === 'user',
      timestamp: msg.create_date,
      isHistoryMessage: true
    };

    createMessageObjects(baseMessage).forEach((segment, idx) => {
      normalized.push({
        ...segment,
        id: idx === 0 ? baseId : `${baseId}-seg${idx}`,
        order: sequence++
      });
    });
  });

  return normalized
    .sort((a, b) => {
      const timeA = new Date(a.timestamp || 0).getTime();
      const timeB = new Date(b.timestamp || 0).getTime();

      if (!Number.isNaN(timeA) && !Number.isNaN(timeB) && timeA !== timeB) {
        return timeA - timeB;
      }

      if (Number.isNaN(timeA) && !Number.isNaN(timeB)) {
        return -1;
      }

      if (!Number.isNaN(timeA) && Number.isNaN(timeB)) {
        return 1;
      }

      return (a.order ?? 0) - (b.order ?? 0);
    })
    .map(({ order, ...rest }) => rest);
};

// Error message component for access denied
const AccessDeniedMessage = ({ message }) => {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      padding: '20px',
      textAlign: 'center',
      color: '#ff3b30'
    }}>
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="15" y1="9" x2="9" y2="15"></line>
        <line x1="9" y1="9" x2="15" y2="15"></line>
      </svg>
      <h3 style={{ marginTop: '16px' }}>Truy cập bị từ chối</h3>
      <p>{message || 'Chatbot này không khả dụng cho tên miền của bạn'}</p>
    </div>
  );
};

function App() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [config, setConfig] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [chatbotId, setChatbotId] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [consecutiveErrors, setConsecutiveErrors] = useState(0);
  const [socketConnected, setSocketConnected] = useState(false);
  const [socketConnecting, setSocketConnecting] = useState(false);
  const [lastConnectionCheck, setLastConnectionCheck] = useState(Date.now());
  const [lastUserActivity, setLastUserActivity] = useState(Date.now());
  const [isUserActive, setIsUserActive] = useState(true);
  const [lastAutoReconnectAttempt, setLastAutoReconnectAttempt] = useState(0);
  const [messageQueue, setMessageQueue] = useState([]); // Queue for pending messages
  const [isProcessingQueue, setIsProcessingQueue] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const bottomRef = useRef(null);
  const chatContainerRef = useRef(null);
  const hasHistoryRef = useRef(false);
  const lastMessageRef = useRef(null); // Track last message to avoid duplicates
  const lastSentUserMessageId = useRef(null); // Track last sent user message for proper ordering
  const pollHistoryTimeoutRef = useRef(null);
  const lastMessageCountRef = useRef(0);
  const isWaitingForResponseRef = useRef(false);
  const responseTimeoutRef = useRef(null);

  // Initialize socket connection
  useEffect(() => {
    const initializeSocket = async () => {
      if (!API_BASE_URL) return;

      setSocketConnecting(true);
      try {
        await socketService.connect(SOCKET_URL.replaceAll('/api', ''));
        setSocketConnected(true);
        logger.log('Socket service connected successfully');
      } catch (error) {
        logger.error('Failed to connect socket:', error);
        setSocketConnected(false);
      } finally {
        setSocketConnecting(false);
      }
    };

    // Setup socket message handler
    const unsubscribeMessage = socketService.onMessage((messageData) => {
      logger.log('Received socket message:', messageData);

      // Ensure socket message has a current timestamp to always appear at the end
      const now = new Date().toISOString();
      const messageWithTimestamp = {
        ...messageData,
        timestamp: messageData.timestamp || now // Use current time if no timestamp
      };

      console.log('🔍 Socket message details:', {
        hasTimestamp: !!messageData.timestamp,
        originalTimestamp: messageData.timestamp,
        finalTimestamp: messageWithTimestamp.timestamp,
        text: messageData.text?.substring(0, 50),
        sent: messageData.sent
      });

      const processedMessages = createMessageObjects(messageWithTimestamp);

      if (processedMessages.length === 0) {
        console.log('Empty socket message ignored');
        return;
      }

      if (isDuplicateMessage(messageWithTimestamp)) {
        console.log('Duplicate socket message ignored:', messageWithTimestamp);
        return;
      }

      if (pollHistoryTimeoutRef.current || responseTimeoutRef.current) {
        stopPollingHistory();
        console.log('🔥 Socket message received - immediately stopping all polling');
      }

      if (processedMessages.some(msg => !msg.sent) && isWaitingForResponseRef.current) {
        setIsTyping(false);
        isWaitingForResponseRef.current = false;
        setConsecutiveErrors(0);
        stopPollingHistory();
        logger.log('✅ Bot response received via socket - stopping polling');
      }

      setMessages(prev => {
        // Helper to check for duplicates based on content
        const isDuplicateContent = (msg1, msg2) => {
          const normalize = (text) => text.trim().toLowerCase().replace(/\s+/g, ' ');
          return normalize(msg1.text) === normalize(msg2.text) && 
                 msg1.sent === msg2.sent;
        };

        // Check for duplicates before adding
        const hasDuplicate = prev.some(msg => 
          processedMessages.some(newMsg => isDuplicateContent(msg, newMsg))
        );

        if (hasDuplicate) {
          console.log('🚫 Duplicate message prevented in socket handler');
          return prev;
        }

        // Simply append socket messages at the end with proper timestamps
        const newMessages = processedMessages.map(msg => ({
          ...msg,
          isSocketMessage: true,
          // Ensure timestamp is always set and is current time
          timestamp: msg.timestamp || now
        }));

        console.log('➕ Adding socket messages at end:', newMessages.length);
        
        const updated = [...prev, ...newMessages];
        
        if (updated.length > 0) {
          lastMessageRef.current = updated[updated.length - 1];
        }

        lastMessageCountRef.current = updated.length;
        lastSentUserMessageId.current = null; // Clear anchor after adding bot message
        
        return updated;
      });
    });

    // Setup socket connection handler
    const unsubscribeConnection = socketService.onConnectionChange((status, reason) => {
      setSocketConnected(status === 'connected');
      setLastConnectionCheck(Date.now()); // Update connection check time

      if (status === 'connected' && sessionId) {
        socketService.joinConversation(sessionId);
        console.log('🔌 Socket reconnected and rejoined conversation');
      }

      if (status === 'disconnected') {
        console.log('🔌 Socket disconnected:', reason);
        // Don't immediately start polling, let the socket try to reconnect first
      }

      console.log('Socket connection status changed:', status, reason);
    });

    initializeSocket();

    // Cleanup on unmount
    return () => {
      unsubscribeMessage();
      unsubscribeConnection();
      socketService.disconnect();
      stopPollingHistory(); // Clean up polling on unmount
    };
  }, [API_BASE_URL]);

  // Join conversation when sessionId changes
  useEffect(() => {
    if (sessionId && socketConnected) {
      console.log('Joining socket conversation:', sessionId);
      socketService.joinConversation(sessionId);
    }
  }, [sessionId, socketConnected]);

  // Function to start polling history for new messages
  const startPollingHistory = () => {
    if (pollHistoryTimeoutRef.current) {
      clearTimeout(pollHistoryTimeoutRef.current);
    }

    const pollHistory = async () => {
      if (!sessionId || !isWaitingForResponseRef.current) {
        console.log('⏸️ Polling stopped: sessionId or waiting flag is false');
        return;
      }

      console.log('🔍 Polling history for new messages...');

      try {
        const response = await axios.post(`${API_BASE_URL}/aichat/history`, {
          conversation_id: sessionId,
          page: 1,
          page_size: 20 // Get more messages to ensure we catch the response
        });

        if (response.data.success) {
          const { messages: historyMessages } = response.data.data;

          if (historyMessages && historyMessages.length > 0) {
            console.log(`📊 History has ${historyMessages.length} messages`);
            const normalizedMessages = normalizeHistoryMessages(historyMessages);
            console.log(`📊 Normalized to ${normalizedMessages.length} messages`);

            // Find new bot messages that we don't have yet
            const currentMessageIds = new Set(messages.map(m => m.id));
            const newBotMessages = normalizedMessages.filter(msg => 
              !msg.sent && !currentMessageIds.has(msg.id)
            );

            if (newBotMessages.length > 0) {
              console.log(`✅ Found ${newBotMessages.length} new bot messages!`);
              
              // Simply add new bot messages to existing messages
              setMessages(prev => {
                // Helper to normalize text for comparison
                const normalizeText = (text) => text.trim().toLowerCase().replace(/\s+/g, ' ');
                
                // Create a set of existing message keys to avoid duplicates
                const existingKeys = new Set(
                  prev.map(msg => `${msg.sent ? 'user' : 'bot'}:${normalizeText(msg.text)}`)
                );
                
                // Add only truly new bot messages
                const messagesToAdd = newBotMessages.filter(msg => {
                  const key = `${msg.sent ? 'user' : 'bot'}:${normalizeText(msg.text)}`;
                  return !existingKeys.has(key);
                });
                
                if (messagesToAdd.length === 0) {
                  console.log('⚠️ All bot messages already exist, skipping merge');
                  return prev;
                }
                
                console.log(`➕ Adding ${messagesToAdd.length} new bot messages`);
                
                // Append new messages and sort by timestamp
                const updated = [...prev, ...messagesToAdd].sort((a, b) => {
                  const timeA = new Date(a.timestamp || 0).getTime();
                  const timeB = new Date(b.timestamp || 0).getTime();
                  // Handle messages without timestamp (put them at the end)
                  if (!a.timestamp) return 1;
                  if (!b.timestamp) return -1;
                  return timeA - timeB;
                });
                
                console.log(`📊 Total messages: ${updated.length} (added ${messagesToAdd.length} new)`);
                return updated;
              });
              
              lastMessageRef.current = newBotMessages[newBotMessages.length - 1];
              setIsTyping(false);
              isWaitingForResponseRef.current = false;
              lastMessageCountRef.current = normalizedMessages.length;
              setConsecutiveErrors(0);

              if (pollHistoryTimeoutRef.current) {
                clearTimeout(pollHistoryTimeoutRef.current);
                pollHistoryTimeoutRef.current = null;
              }
              if (responseTimeoutRef.current) {
                clearTimeout(responseTimeoutRef.current);
                responseTimeoutRef.current = null;
              }

              forceScroll(bottomRef.current);
              console.log('✅ New bot response received via history polling');
              
              return;
            } else {
              console.log('⏳ No new bot messages yet, continuing to poll...');
            }
          }
        }
      } catch (error) {
        console.error('❌ Error polling history:', error);
      }

      // Continue polling if still waiting for response
      if (isWaitingForResponseRef.current) {
        pollHistoryTimeoutRef.current = setTimeout(pollHistory, 1500); // Poll every 1.5 seconds
        console.log('🔄 Scheduled next poll in 1.5s');
      }
    };

    // Start first poll after a delay to give server time to save messages
    pollHistoryTimeoutRef.current = setTimeout(pollHistory, 2000);
    console.log('📊 Started polling history for new messages (first poll in 2s)');
  };

  // Function to stop polling history
  const stopPollingHistory = () => {
    if (pollHistoryTimeoutRef.current) {
      clearTimeout(pollHistoryTimeoutRef.current);
      pollHistoryTimeoutRef.current = null;
    }
    if (responseTimeoutRef.current) {
      clearTimeout(responseTimeoutRef.current);
      responseTimeoutRef.current = null;
    }
    isWaitingForResponseRef.current = false;
    console.log('🛑 Stopped polling history');
  };

  // Function to check for duplicate messages
  const isDuplicateMessage = (newMessage) => {
    if (!lastMessageRef.current) return false;

    const segments = createMessageObjects(newMessage);

    if (segments.length === 0) {
      return true;
    }

    const lastSegment = segments[segments.length - 1];
    const lastMsg = lastMessageRef.current;
    const timeDiff = Math.abs(new Date(lastSegment.timestamp) - new Date(lastMsg.timestamp));

    return (
      lastMsg.text === lastSegment.text &&
      lastMsg.sent === lastSegment.sent &&
      timeDiff < 5000
    );
  };

  // Function to clear cache and reset connection
  const clearCacheAndReset = () => {
    // Clear axios cache
    delete axios.defaults.headers.common['Authorization'];

    // Force browser to clear cache for API calls
    if ('caches' in window) {
      caches.keys().then(function(names) {
        names.forEach(function(name) {
          caches.delete(name);
        });
      });
    }

    // Reset consecutive errors
    setConsecutiveErrors(0);

    console.log('Cache cleared and connection reset');
  };

  // Function to check if current URL is allowed
  const checkUrlAccess = (config) => {
    if (!config) return false;

    const referrer = document.referrer;

    // If no referrer is provided (direct access), deny
    // if (!referrer) {
    //   console.log('Direct access detected - denying access');
    //   return false;
    // }
    return true;
    try {
      const referrerUrl = new URL(referrer);
      if (referrerUrl.hostname === 'xneva.vn' || referrerUrl.hostname === 'tro-ly-neva.nodo.vn') return true;
    } catch (error) {
      console.error('Invalid referrer URL:', referrer, error);
      return false;
    }

    // Check if referrer URL is in allowed list
    if (config.urls_allowed) {
      const allowedUrls = config.urls_allowed.split(',').map(url => url.trim());
      if (allowedUrls.length === 0) return false;
      try {
        const referrerUrl = new URL(referrer);
        const referrerHost = referrerUrl.hostname;
        return allowedUrls.some(url => {
          try {
            const allowedHost = new URL(url).hostname;
            return referrerHost === allowedHost;
          } catch (e) {
            // If URL parsing fails, try direct string comparison
            return referrer.includes(url);
          }
        });
      } catch (error) {
        console.error('Error parsing referrer URL:', error);
        // If we can't parse the referrer URL, allow access
        return false;
      }
    }else{
      return false;
    }
    return false;
  };

  const checkHistory = async (currentSessionId) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/aichat/history`, {
        conversation_id: currentSessionId,
        page: 1,
        page_size: PAGE_SIZE
      });

      if (response.data.success) {
        const { messages: historyMessages } = response.data.data;
        if (historyMessages && historyMessages.length > 0) {
          const normalizedMessages = normalizeHistoryMessages(historyMessages);

          if (normalizedMessages.length > 0) {
            setMessages(normalizedMessages);
            lastMessageRef.current = normalizedMessages[normalizedMessages.length - 1];
            lastMessageCountRef.current = normalizedMessages.length;
            return true;
          }
        }
      }
      return false;
    } catch (error) {
      console.error('Error checking history:', error);
      return false;
    }
  };

  const loadMoreHistory = async (page) => {
    if (!sessionId || isLoadingHistory || page === 1) return;

    setIsLoadingHistory(true);
    try {
      const oldScrollHeight = chatContainerRef.current?.scrollHeight || 0;

      const response = await axios.post(`${API_BASE_URL}/aichat/history`, {
        conversation_id: sessionId,
        page: page,
        page_size: PAGE_SIZE
      });

      if (response.data.success) {
        const { messages: historyMessages, total_messages } = response.data.data;

        if (historyMessages && historyMessages.length > 0) {
          const normalizedMessages = normalizeHistoryMessages(historyMessages);

          setMessages(prev => {
            const updated = [...normalizedMessages, ...prev];
            lastMessageCountRef.current = updated.length;
            return updated;
          });

          setHasMoreMessages(page * PAGE_SIZE < total_messages);
          setCurrentPage(page);

          if (chatContainerRef.current) {
            const newScrollHeight = chatContainerRef.current.scrollHeight;
            chatContainerRef.current.scrollTop = newScrollHeight - oldScrollHeight;
          }
        }
      }
    } catch (error) {
      console.error('Error loading more history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      try {
        setIsLoading(true);
        const pathname = window.location.pathname;
        const currentChatbotId = pathname.split('/').pop();
        setChatbotId(currentChatbotId);

        // Lấy query parameters từ URL
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('access_token');
        const contextBefor = urlParams.get('context_befor');

        // Xóa query parameter context_befor sau khi đã lấy giá trị
        if (contextBefor) {
          urlParams.delete('context_befor');
          const newUrl = `${window.location.pathname}${urlParams.toString() ? `?${urlParams.toString()}` : ''}`;
          window.history.replaceState({}, '', newUrl);
        }

        setAccessToken(token);

        let currentSessionId = localStorage.getItem(currentChatbotId);
        if (!currentSessionId) {
          currentSessionId = uuidv4();
          localStorage.setItem(currentChatbotId, currentSessionId);
        }
        setSessionId(currentSessionId);

        try {
          const referrer = document.referrer || '';
          const configResponse = await axios.post(`${API_BASE_URL}/aichat/chatbot`, {
            chatbot_id: currentChatbotId,
            access_token: token
          }, {
            params: { referrer }
          });

          const configData = configResponse.data.data;
          setConfig(configData);

          // Check if current URL is allowed
          if (!checkUrlAccess(configData)) {
            setAccessDenied(true);
            setErrorMessage('Trợ lý AI này không khả dụng cho tên miền của bạn');
            setIsLoading(false);
            return;
          }

          const hasHistory = await checkHistory(currentSessionId);

          if (!hasHistory && configData) {
            const now = new Date().toISOString();
            const welcomeMessage = {
              id: Date.now(),
              text: configData.welcome_message,
              sent: false,
              timestamp: now
            };
            setMessages([welcomeMessage]);
            lastMessageRef.current = welcomeMessage;
            lastMessageCountRef.current = 1;
          }
        } catch (error) {
          if (error.response && error.response.status === 403) {
            setAccessDenied(true);
            setErrorMessage(error.response.data.message || 'Hội thoại hiện không khả dụng, Vui lòng liên hệ quản trị viên!');
          } else {
            console.error('Error initializing chat:', error);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    initialize();
  }, []);

  useEffect(() => {
    if (messages.length > 0 || isTyping) {
      forceScroll(bottomRef.current);
    }
  }, [messages, isTyping]);

  // Monitor consecutive errors and auto-clear cache
  useEffect(() => {
    if (consecutiveErrors >= 5) {
      console.log('Too many consecutive errors detected, auto-clearing cache...');
      clearCacheAndReset();

      // Show a system message about cache clearing
      setMessages(prev => [...prev, 
        { 
          id: Date.now(), 
          text: "Hệ thống đã tự động xóa cache do nhiều lỗi liên tiếp. Vui lòng thử gửi tin nhắn lại.", 
          sent: false, 
          timestamp: new Date().toISOString(),
          isSystemMessage: true
        }
      ]);
    }
  }, [consecutiveErrors]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      stopPollingHistory();
    };
  }, []);

  // Function to automatically submit a message
  const autoSubmitMessage = (message) => {
    if (!sessionId || isTyping) return;
    
    setInput(message);
    
    // Use setTimeout to ensure state update before sending
    setTimeout(() => {
      handleSend(message);
    }, 100);
  };

  // Listen for messages from the parent window
  useEffect(() => {
    const handleIncomingMessage = (event) => {
      // Define allowed origins
      const allowedOrigins = [
        'https://chat-view.nodo.vn',
        'https://chat-view.xneva.vn',
        'http://localhost:1055',
        'http://localhost:8070',
        'https://landing.ffactory.online',
        'https://tro-ly-neva.nodo.vn',
        'https://xneva.vn'
      ];
      
      // Check if the origin is allowed
      if (allowedOrigins.includes(event.origin)) {
        // console.log('Received message from parent:', event.data);
        
        // Check if it's a product order message
        if (event.data && event.data.type === 'productOrder' && event.data.product) {
          const productData = event.data.product;
          const orderMessage = `Tôi muốn mua sản phẩm ${productData} hãy tạo đơn cho tôi`;
          
          // console.log('Auto-submitting order message:', orderMessage);
          autoSubmitMessage(orderMessage);
        }
      } else {
        console.warn('Blocked message from untrusted origin:', event.origin);
      }
    };

    window.addEventListener('message', handleIncomingMessage);
    
    return () => {
      window.removeEventListener('message', handleIncomingMessage);
    };
  }, [sessionId, isTyping]); // Add dependencies to ensure we have current state

  // Listen for link click events from MarkdownMessage component
  useEffect(() => {
    const handleLinkClick = (event) => {
      const { url } = event.detail;
      
      if (!url) return;
      
      // Kiểm tra nếu là link xneva.vn hoặc tro-ly-neva
      const isXnevaLink = url.startsWith('https://xneva.vn/') || 
                          url.startsWith('https://tro-ly-neva.nodo.vn/');
      
      if (isXnevaLink) {
        // Mở trong parent window (nếu đang trong iframe)
        if (window.parent && window.parent !== window) {
          window.parent.location = url;
        } else {
          window.location.href = url;
        }
      } else {
        // Mở link bình thường trong tab mới
        const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
        
        // Fallback nếu bị chặn popup
        // if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        //   // Thử phương án khác: tạo thẻ a ẩn và click
        //   const link = document.createElement('a');
        //   link.href = url;
        //   link.target = '_blank';
        //   link.rel = 'noopener noreferrer';
        //   document.body.appendChild(link);
        //   link.click();
        //   document.body.removeChild(link);
        // }
      }
    };

    window.addEventListener('openLink', handleLinkClick);
    
    return () => {
      window.removeEventListener('openLink', handleLinkClick);
    };
  }, []);

  const handleScroll = () => {
    if (!chatContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    const scrolledFromBottom = scrollHeight - (scrollTop + clientHeight);
    setShowScrollButton(scrolledFromBottom > 100);

    if (scrollTop === 0 && hasMoreMessages && !isLoadingHistory) {
      // loadMoreHistory(currentPage + 1);
    }
  };

  // Manual reconnect function for users
  const handleReconnect = async () => {
    // Prevent multiple concurrent reconnect attempts
    if (socketConnecting) {
      console.log('🔄 Reconnect already in progress, skipping...');
      return;
    }
    
    console.log('🔄 User triggered manual reconnect...');
    setSocketConnecting(true);
    
    try {
      // Use the reconnect method from socketService
      await socketService.reconnect();
      setLastConnectionCheck(Date.now());
      console.log('✅ Manual reconnect completed');
    } catch (error) {
      console.error('❌ Manual reconnect failed:', error);
      // If reconnect fails, try full reinitialize
      try {
        await socketService.connect(SOCKET_URL.replaceAll('/api', ''));
        console.log('✅ Full reconnect completed');
      } catch (reinitError) {
        console.error('❌ Full reconnect also failed:', reinitError);
      }
    } finally {
      setSocketConnecting(false);
    }
  };

  // Check connection health periodically
  useEffect(() => {
    const connectionHealthCheck = setInterval(() => {
      const now = Date.now();
      const timeSinceLastCheck = now - lastConnectionCheck;
      
      // Check every 2 minutes if connection is stale
      if (timeSinceLastCheck > 120000) { // 2 minutes
        console.log('🔍 Periodic connection health check...');
        
        // If socket claims to be connected but we haven't seen activity
        if (socketConnected && socketService.isSocketConnected()) {
          setLastConnectionCheck(now);
        } else if (socketConnected && !socketService.isSocketConnected()) {
          console.warn('🚨 Connection state mismatch detected in health check');
          setSocketConnected(false);
        }
      }
    }, 60000); // Check every minute
    
    return () => clearInterval(connectionHealthCheck);
  }, [socketConnected, lastConnectionCheck]);

  // User activity detection and auto-reconnect
  useEffect(() => {
    let activityTimeout;
    
    const updateUserActivity = () => {
      const now = Date.now();
      setLastUserActivity(now);
      setIsUserActive(true);
      
      // Clear existing timeout
      if (activityTimeout) {
        clearTimeout(activityTimeout);
      }
      
      // Auto reconnect if socket is disconnected and user is active
      if (!socketConnected && !socketConnecting && isUserActive) {
        const timeSinceLastAttempt = now - lastAutoReconnectAttempt;
        
        // Only attempt auto reconnect if it's been at least 10 seconds since last attempt
        if (timeSinceLastAttempt > 10000) {
          console.log('🎯 User activity detected while offline - attempting auto reconnect...');
          setLastAutoReconnectAttempt(now);
          handleReconnect();
        } else {
          console.log('🎯 User activity detected but reconnect throttled (last attempt was', timeSinceLastAttempt, 'ms ago)');
        }
      }
      
      // Set user as inactive after 5 minutes of no activity
      activityTimeout = setTimeout(() => {
        setIsUserActive(false);
        console.log('💤 User inactive - pausing auto reconnect attempts');
      }, 300000); // 5 minutes
    };

    // Activity events to monitor
    const activityEvents = [
      'mousedown', 
      'mousemove', 
      'keypress', 
      'scroll', 
      'touchstart', 
      'click',
      'focus'
    ];

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, updateUserActivity, true);
    });

    // Initial activity
    updateUserActivity();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, updateUserActivity, true);
      });
      if (activityTimeout) {
        clearTimeout(activityTimeout);
      }
    };
  }, [socketConnected, socketConnecting, isUserActive]);

  // Process message queue when conditions are right - DISABLED
  // useEffect(() => {
  //   if (messageQueue.length > 0 && !isTyping && !isProcessingQueue) {
  //     logger.log('🔄 Queue has messages and bot is not typing, processing queue...');
  //     processMessageQueue();
  //   }
  // }, [messageQueue.length, isTyping, isProcessingQueue]);

  const scrollToBottom = () => {
    forceScroll(bottomRef.current);
  };

  const handleImageClick = (src) => {
    setModalImage(src);
  };

  const handleCloseModal = () => {
    setModalImage(null);
  };

  // Function to process message queue - DISABLED
  // const processMessageQueue = async () => {
  //   if (isProcessingQueue || messageQueue.length === 0 || isTyping) {
  //     return;
  //   }

  //   setIsProcessingQueue(true);
  //   logger.log('📋 Processing message queue, items:', messageQueue.length);
    
  //   // Add 1 second delay for smooth animation
  //   await new Promise(resolve => setTimeout(resolve, 1000));
    
  //   // Get the first message from queue
  //   const nextMessage = messageQueue[0];
    
  //   // Remove processed message from queue
  //   setMessageQueue(prev => prev.slice(1));
    
  //   // Update message status to "sending"
  //   setMessages(prev => prev.map(msg => 
  //     msg.id === nextMessage.id 
  //       ? { ...msg, isQueued: false, isSending: true }
  //       : msg
  //   ));
    
  //   // Process the message
  //   await sendMessageToAPI(nextMessage);
    
  //   setIsProcessingQueue(false);
  // };

  // Function to add message to queue - DISABLED
  // const addMessageToQueue = (messageText) => {
  //   const queuedMessage = {
  //     id: Date.now() + Math.random(),
  //     text: messageText,
  //     timestamp: new Date().toISOString(),
  //     status: 'queued'
  //   };
    
  //   setMessageQueue(prev => [...prev, queuedMessage]);
  //   logger.log('📋 Message added to queue:', messageText);
    
  //   // Show queued message in UI immediately (without timestamp)
  //   const displayMessage = {
  //     id: queuedMessage.id,
  //     text: messageText,
  //     sent: true,
  //     timestamp: null, // No timestamp for queued messages
  //     isQueued: true
  //   };
    
  //   setMessages(prev => [...prev, displayMessage]);
  //   forceScroll(bottomRef.current);
    
  //   return queuedMessage.id;
  // };

  // Function to actually send message to API
  const sendMessageToAPI = async (queuedMessage) => {
    let userMessage = queuedMessage.text;
    
    // Update last message reference to prevent socket duplicates
    const actualTimestamp = new Date().toISOString(); // Generate timestamp when actually sending
    const userMessageData = {
      id: queuedMessage.id,
      text: userMessage,
      sent: true,
      timestamp: actualTimestamp
    };
    
    lastMessageRef.current = userMessageData;
    lastSentUserMessageId.current = queuedMessage.id; // Store user message ID for proper bot response insertion
    
    // Update message status to "sent" and add timestamp
    setMessages(prev => prev.map(msg => 
      msg.id === queuedMessage.id 
        ? { ...msg, isQueued: false, isSending: false, timestamp: actualTimestamp }
        : msg
    ));

    setIsTyping(true);
    isWaitingForResponseRef.current = true; // Mark that we're waiting for a response
    console.log('⏳ Waiting for bot response...');

    // Don't set up timeout yet - wait for API to complete first

    try {
      const referrer = document.referrer || '';
      const queryParams = new URLSearchParams({
        conversationId: sessionId,
        referrer: referrer
      }).toString();

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000);

      const response = await axios.post(
        `${API_BASE_URL}/api/${chatbotId}/assistant?${queryParams}`,
        {
          message: userMessage,
          access_token: accessToken
        },
        {
          signal: controller.signal,
          timeout: 300000,
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'X-Request-Time': Date.now().toString()
          }
        }
      );

      clearTimeout(timeoutId);
      // Don't set isTyping to false yet - keep showing typing while waiting for socket response

      if (response.data && response.data.success) {
        setConsecutiveErrors(0);
        logger.log('✅ Message sent to API successfully, waiting for socket response...');
        
        // NOW set up fallback timeout AFTER API completes successfully
        // Wait for socket response before starting polling
        responseTimeoutRef.current = setTimeout(() => {
          if (isWaitingForResponseRef.current) {
            const isSocketWorking = socketConnected && socketService.isSocketConnected();
            
            if (!isSocketWorking) {
              logger.log('⚠️ Socket disconnected after API success, starting history polling');
              startPollingHistory();
            } else {
              logger.log('⚠️ No socket response after API success, starting polling as backup');
              startPollingHistory();
            }
          }
        }, 1000); // Wait 1s after API success for socket response
        setIsTyping(false); 
        // Don't start polling immediately even if socket is disconnected
        // Give socket time to reconnect and deliver the message
        if (!socketConnected || !socketService.isSocketConnected()) {
          logger.log('⚠️ Socket not connected, but will wait before polling');
        }
      } else {
        setIsTyping(false); // Only stop typing if API failed
        throw new Error('API request failed');
      }
    } catch (error) {
      logger.error('Error sending message:', error);
      setIsTyping(false);
      
      const newConsecutiveErrors = consecutiveErrors + 1;
      setConsecutiveErrors(newConsecutiveErrors);
      
      if (newConsecutiveErrors >= 3) {
        logger.log('Too many consecutive errors, clearing cache...');
        clearCacheAndReset();
      }
      
      if (error.response && error.response.status === 403) {
        setAccessDenied(true);
        setErrorMessage(error.response.data.message || 'Access denied');
        stopPollingHistory();
        return;
      }
      
      if (!socketConnected || !socketService.isSocketConnected()) {
        logger.log('🔄 API error but socket disconnected, starting polling to check for response');
        startPollingHistory();
        
        setTimeout(() => {
          if (isWaitingForResponseRef.current) {
            stopPollingHistory();
            
            let errorMessage = config?.chatbot_error_message || "Đã xảy ra lỗi. Vui lòng thử lại.";
            
            if (error.response?.status === 502) {
              errorMessage = "Server tạm thời không khả dụng. Vui lòng thử lại sau.";
            } else if (error.code === 'ECONNABORTED' || error.name === 'AbortError') {
              errorMessage = "Yêu cầu quá thời gian chờ. Vui lòng thử lại.";
            }
            
            setMessages(prev => [...prev, 
              { 
                id: Date.now() + 1, 
                text: errorMessage, 
                sent: false, 
                timestamp: new Date().toISOString(),
                isError: true
              }
            ]);
            
            forceScroll(bottomRef.current);
          }
        }, 10000);
      } else {
        stopPollingHistory();
        
        let errorMessage = config?.chatbot_error_message || "Đã xảy ra lỗi. Vui lòng thử lại.";
        
        if (error.response?.status === 502) {
          errorMessage = "Server tạm thời không khả dụng. Vui lòng thử lại sau.";
        } else if (error.code === 'ECONNABORTED' || error.name === 'AbortError') {
          errorMessage = "Yêu cầu quá thời gian chờ. Vui lòng thử lại.";
        }
        
        setMessages(prev => [...prev, 
          { 
            id: Date.now() + 1, 
            text: errorMessage, 
            sent: false, 
            timestamp: new Date().toISOString(),
            isError: true
          }
        ]);
        
        forceScroll(bottomRef.current);
      }
    }
  };

  const handleSend = async (messageText) => {
    let userMessage = messageText || input.trim();
    if (!userMessage || !sessionId) return;

    // Lấy context_befor từ URL nếu có
    const urlParams = new URLSearchParams(window.location.search);
    const contextBefor = urlParams.get('context_befor');
    
    // Thêm context_befor vào đầu tin nhắn nếu có
    if (contextBefor) {
      userMessage = `${contextBefor}\n${userMessage}`;
      
      // Xóa query parameter sau khi sử dụng
      urlParams.delete('context_befor');
      const newUrl = `${window.location.pathname}${urlParams.toString() ? `?${urlParams.toString()}` : ''}`;
      window.history.replaceState({}, '', newUrl);
    }

    // Chặn gửi tin nhắn khi bot đang typing
    if (isTyping || isProcessingQueue) {
      logger.log('🤖 Bot is typing, please wait...');
      return;
    }

    setInput('');

    // Process message immediately
    logger.log('✅ Bot is free, processing message immediately:', userMessage);
    
    const now = new Date().toISOString();
    const userMessageData = { 
      id: Date.now(), 
      text: userMessage, 
      sent: true, 
      timestamp: now 
    };
    
    setMessages(prev => [...prev, userMessageData]);
    forceScroll(bottomRef.current);

    // Create a queue message object for consistency
    const queueMessage = {
      id: userMessageData.id,
      text: userMessage,
      timestamp: now,
      status: 'immediate'
    };

    await sendMessageToAPI(queueMessage);
  };

  const handleRefreshConversation = async () => {
    const pathname = window.location.pathname;
    const currentChatbotId = pathname.split('/').pop();
    const newSessionId = uuidv4();
    localStorage.setItem(currentChatbotId, newSessionId);
    setSessionId(newSessionId);

    setMessages([]);
    lastMessageRef.current = null;
    lastMessageCountRef.current = 0;
    setCurrentPage(1);
    setHasMoreMessages(true);
    hasHistoryRef.current = false;
    
    // Stop any ongoing polling
    stopPollingHistory();
    
    // Clear cache and reset connection
    clearCacheAndReset();

    if (config) {
      const now = new Date().toISOString();
      const welcomeMessage = {
        id: Date.now(),
        text: config.welcome_message,
        sent: false,
        timestamp: now
      };
      setMessages([welcomeMessage]);
      lastMessageRef.current = welcomeMessage;
      lastMessageCountRef.current = 1;
    }

    forceScroll(bottomRef.current);
  };

  if (isLoading) {
    return (
      <ThemeProvider theme={defaultTheme}>
        <LoadingScreen />
      </ThemeProvider>
    );
  }

  if (accessDenied) {
    return (
      <ThemeProvider theme={defaultTheme}>
        <Container>
          <AccessDeniedMessage message={errorMessage} />
        </Container>
      </ThemeProvider>
    );
  }

  if (!config) {
    return (
      <ThemeProvider theme={defaultTheme}>
        <LoadingScreen />
      </ThemeProvider>
    );
  }

  const currentTheme = createThemeFromConfig(config);

  return (
    <ThemeProvider theme={currentTheme}>
      <Container config={config}>
        <ChatHeader 
          config={config} 
          isTyping={isTyping}
          onRefreshConversation={handleRefreshConversation}
          socketConnected={socketConnected}
          socketConnecting={socketConnecting}
        />
        <ChatMessages
          messages={messages}
          isTyping={isTyping}
          isLoadingHistory={isLoadingHistory}
          config={config}
          chatContainerRef={chatContainerRef}
          bottomRef={bottomRef}
          handleScroll={handleScroll}
          modalImage={modalImage}
          onImageClick={handleImageClick}
          onCloseModal={handleCloseModal}
        />
        <ScrollButtonWrapper>
          <ScrollToBottom 
            isVisible={showScrollButton}
            onClick={scrollToBottom}
          />
        </ScrollButtonWrapper>
        <ChatInput
          input={input}
          setInput={setInput}
          handleSend={handleSend}
          isTyping={isTyping}
          config={config}
        />
      </Container>
    </ThemeProvider>
  );
}

export default App;
